import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Button } from './components/ui/button';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './components/ui/dropdown-menu';
import { cn } from './lib/utils';
import {
  CalendarIcon,
  PeopleIcon,
  DescriptionIcon,
  SettingsIcon,
  BarChartIcon,
  LogoutIcon,
  AccountCircleIcon,
  KeyboardArrowDownIcon,
  MenuIcon,
  CloseIcon,
  AutoAwesomeIcon,
  NotificationsIcon
} from './components/icons';
import { FileText, LayoutDashboard } from 'lucide-react'; // eslint-disable-line no-unused-vars
import SidebarCalendarStatus from './components/SidebarCalendarStatus';
import MeetingLimitIndicator from './components/MeetingLimitIndicator';
import UpgradeModal from './components/UpgradeModal';
import QuickActionPanel from './components/QuickActionPanel';
import { CheckCircle2 } from 'lucide-react';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_TIMEOUT_MS = 25 * 60 * 1000;    // 25 minutes

const navItems = [
  { label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/dashboard' },
  { label: 'Meetings', icon: <CalendarIcon />, path: '/meetings' },
  { label: 'Clients', icon: <PeopleIcon />, path: '/clients' },
  { label: 'Action Items', icon: <NotificationsIcon />, path: '/action-items' },
  { label: 'Templates', icon: <DescriptionIcon />, path: '/templates' },
  { label: 'Documents', icon: <FileText size={20} />, path: '/documents' },
  { label: 'Ask Advicly', icon: <AutoAwesomeIcon />, path: '/ask-advicly' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const analyticsNav = [
  { label: 'Client Pipeline', icon: <BarChartIcon />, path: '/pipeline' },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeSuccessMessage, setUpgradeSuccessMessage] = useState('');
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [showQuickActionPanel, setShowQuickActionPanel] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, signOut, user } = useAuth();

  // Cmd+K keyboard shortcut for Quick Action Panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowQuickActionPanel(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const resetInactivityTimers = useCallback(() => {
    setShowInactivityWarning(false);
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    warningTimerRef.current = setTimeout(() => {
      setShowInactivityWarning(true);
    }, WARNING_TIMEOUT_MS);
    logoutTimerRef.current = setTimeout(() => {
      setShowInactivityWarning(false);
      (signOut || logout)();
    }, INACTIVITY_TIMEOUT_MS);
  }, [signOut, logout]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    const handleActivity = () => resetInactivityTimers();
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    resetInactivityTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      clearTimeout(warningTimerRef.current);
      clearTimeout(logoutTimerRef.current);
    };
  }, [resetInactivityTimers]);

  // Detect successful upgrade from Stripe checkout
  useEffect(() => {
    const upgraded = searchParams.get('upgraded');
    if (upgraded === 'true') {
      console.log('🎉 User successfully upgraded to Professional!');
      setUpgradeSuccessMessage('🎉 Welcome to Professional! You now have unlimited AI-transcribed meetings.');

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setUpgradeSuccessMessage('');
      }, 5000);

      // Clean up URL (remove ?upgraded=true parameter)
      searchParams.delete('upgraded');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogout = () => {
    logout();
  };

  // Get user's display name from Supabase Auth user_metadata
  const getUserDisplayName = () => {
    // Check user_metadata first (where updateUser stores it)
    const metaName = user?.user_metadata?.full_name || user?.user_metadata?.name;
    if (metaName) return metaName;

    // Fallback to email username
    if (user?.email) {
      return user.email.split('@')[0];
    }

    return 'User';
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="flex min-h-screen font-sans layout-bg">
      {/* AppBar - Glass-Morphism */}
      <header className="fixed top-0 left-0 sm:left-64 right-0 z-50 bg-card/90 border-b border-border/20 backdrop-blur-lg shadow-card">
        <div className="flex items-center justify-between px-6 py-4 h-18">
          {/* Hamburger for mobile */}
          <div className="block sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(!open)}
              className="text-muted-foreground hover:text-foreground"
            >
              <MenuIcon />
            </Button>
          </div>

          <div className="flex-1" />

          {/* User Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-3 px-3 py-2 rounded-full hover:bg-accent"
              >
                <Avatar className="w-8 h-8 bg-primary text-primary-foreground text-sm font-semibold rounded-full">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold rounded-full">
                    {getUserInitials(getUserDisplayName())}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold text-foreground">
                    {getUserDisplayName()}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {user?.email || 'user@example.com'}
                  </span>
                </div>
                <KeyboardArrowDownIcon className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 mt-2">
              <DropdownMenuItem className="flex items-center gap-2">
                <AccountCircleIcon className="w-4 h-4" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2"
                onClick={handleLogout}
              >
                <LogoutIcon className="w-4 h-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Sidebar Drawer - Glass-Morphism */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card/90 border-r border-border/20 backdrop-blur-lg shadow-card z-40 pt-20 hidden sm:block">
        <div className="flex flex-col h-full">
          {/* Logo */}
          

          {/* Navigation Items */}
          <nav className="flex-1 overflow-y-auto py-6">
            <div className="px-4 flex flex-col gap-3">
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      "sidebar-item text-base px-4 py-3 font-medium",
                      isActive && "active text-base"
                    )
                  }
                >
                  <span className="w-5 h-5">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Analytics Section */}
            <div className="mt-6 px-4">
              <div className="mb-2">
                <span className="label">
                  Analytics
                </span>
              </div>
              <div className="space-y-1">
                {analyticsNav.map((item) => (
                  <NavLink
                    key={item.label}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "sidebar-item",
                        isActive && "active"
                      )
                    }
                  >
                    <span className="w-5 h-5">
                      {item.icon}
                    </span>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>

          {/* Sticky Footer with Quick Access and Logo */}
          <div className="border-t border-border/50">
            {/* Meeting Limit Indicator */}
            <div className="p-3">
              <MeetingLimitIndicator onUpgradeClick={() => setShowUpgradeModal(true)} />
            </div>

            {/* Calendar Integrations Status */}
            <div className="p-3">
              <SidebarCalendarStatus />
            </div>

            {/* Advicly Logo */}
            <div className="px-4 py-6">
              <div className="flex items-center justify-center">
                <img
                  src="https://xjqjzievgepqpgtggcjx.supabase.co/storage/v1/object/sign/assets/Advicly%20(400%20x%20100%20px).svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81NTIwYjQ4Yi00ZTE5LTQ1ZGQtYTYxNC1kZTk5NzMwZTBiMmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhc3NldHMvQWR2aWNseSAoNDAwIHggMTAwIHB4KS5zdmciLCJpYXQiOjE3NjUyODM0NTcsImV4cCI6MTgyODM1NTQ1N30.yJa3VGx3OEyV3yrCDZ20KS2FMKr6fNiNp7McqkQ17jo"
                  alt="Advicly"
                  className="w-full max-w-[180px]"
                />
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-0 sm:ml-64 bg-background min-h-screen pt-20 overflow-x-hidden">
        <Outlet />
      </main>

      {/* Mobile Menu Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 sm:hidden">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-card/95 backdrop-blur-lg shadow-large">
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <img
                src="https://xjqjzievgepqpgtggcjx.supabase.co/storage/v1/object/sign/assets/Advicly%20(400%20x%20100%20px).svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81NTIwYjQ4Yi00ZTE5LTQ1ZGQtYTYxNC1kZTk5NzMwZTBiMmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhc3NldHMvQWR2aWNseSAoNDAwIHggMTAwIHB4KS5zdmciLCJpYXQiOjE3NjUyODM0NTcsImV4cCI6MTgyODM1NTQ1N30.yJa3VGx3OEyV3yrCDZ20KS2FMKr6fNiNp7McqkQ17jo"
                alt="Advicly"
                className="h-8 w-auto"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <CloseIcon />
              </Button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4">
              <div className="px-4 space-y-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.label}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "sidebar-item",
                        isActive && "active"
                      )
                    }
                  >
                    <span className="w-5 h-5">
                      {item.icon}
                    </span>
                    {item.label}
                  </NavLink>
                ))}
              </div>

              {/* Analytics Section - Mobile */}
              <div className="mt-6 px-4">
                <div className="mb-2">
                  <span className="label">
                    Analytics
                  </span>
                </div>
                <div className="space-y-1">
                  {analyticsNav.map((item) => (
                    <NavLink
                      key={item.label}
                      to={item.path}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "sidebar-item",
                          isActive && "active"
                        )
                      }
                    >
                      <span className="w-5 h-5">
                        {item.icon}
                      </span>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </nav>

            {/* Sticky Footer - Mobile */}
            <div className="border-t border-border/50">
              {/* Meeting Limit Indicator - Mobile */}
              <div className="p-3">
                <MeetingLimitIndicator onUpgradeClick={() => {
                  setOpen(false);
                  setShowUpgradeModal(true);
                }} />
              </div>

              {/* Calendar Integrations Status - Mobile */}
              <div className="p-3" onClick={() => setOpen(false)}>
                <SidebarCalendarStatus />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      {/* Inactivity Warning Modal */}
      {showInactivityWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border/50 rounded-2xl shadow-large p-8 max-w-sm w-full mx-4 text-center space-y-5">
            <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
              <span className="text-2xl">⏱</span>
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Still there?</h2>
              <p className="text-sm text-muted-foreground">
                You'll be signed out in 5 minutes due to inactivity.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => resetInactivityTimers()}
            >
              Stay signed in
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Success Toast */}
      {upgradeSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{upgradeSuccessMessage}</span>
          </div>
        </div>
      )}

      {/* Quick Action Panel (Cmd+K) */}
      <QuickActionPanel
        isOpen={showQuickActionPanel}
        onClose={() => setShowQuickActionPanel(false)}
      />

    </div>
  );
}